import React from 'react';
import styled from 'styled-components';
import { FiAlertTriangle, FiX } from 'react-icons/fi';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
  padding: 2rem;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    max-width: 90vw;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 193, 7, 0.1);
  border-radius: 0;
  color: var(--warning-color);
  flex-shrink: 0;
`;

const Title = styled.h2`
  font-size: 1.375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const Body = styled.div`
  margin-bottom: 2rem;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const QuizTitle = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: var(--background-secondary);
  border-left: 4px solid var(--warning-color);
  border-radius: 0;
  font-weight: 600;
  color: var(--text-primary);
  word-break: break-word;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.secondary {
    background: var(--background-tertiary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover {
      background: var(--background-secondary);
      border-color: var(--border-dark);
    }
  }

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);

    &:hover {
      background: var(--primary-hover);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(216, 184, 137, 0.3);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: var(--text-primary);
    transform: scale(1.1);
  }
`;

/**
 * Modal for confirming save when duplicate title is detected
 */
function DuplicateTitleConfirmModal({
  title,
  onConfirm,
  onCancel,
  isLoading = false
}) {
  return (
    <ModalOverlay onClick={onCancel}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onCancel} type="button">
          <FiX size={20} />
        </CloseButton>

        <Header>
          <IconWrapper>
            <FiAlertTriangle size={24} />
          </IconWrapper>
          <Title>Duplicate Quiz Title</Title>
        </Header>

        <Body>
          <p>
            A quiz with this title already exists. You can:
          </p>
          <ul style={{ margin: '1rem 0 0 1.5rem', paddingLeft: 0 }}>
            <li style={{ marginBottom: '0.5rem' }}>
              Go back and change the title to make it unique
            </li>
            <li>
              Proceed with saving anyway (not recommended)
            </li>
          </ul>
          <QuizTitle>
            <strong>Existing Quiz Title:</strong>
            <div style={{ marginTop: '0.5rem' }}>{title}</div>
          </QuizTitle>
        </Body>

        <ActionButtons>
          <Button
            className="secondary"
            onClick={onCancel}
            disabled={isLoading}
            type="button"
          >
            <FiX size={16} />
            Go Back and Change
          </Button>
          <Button
            className="primary"
            onClick={onConfirm}
            disabled={isLoading}
            type="button"
          >
            {isLoading ? 'Saving...' : 'Proceed Anyway'}
          </Button>
        </ActionButtons>
      </ModalContent>
    </ModalOverlay>
  );
}

export default DuplicateTitleConfirmModal;
